import React, { useEffect, useRef, useState } from "react";

// Multi-step flows where a guest is genuinely mid-form (typing a group name,
// picking an icon) get asked "still there?" instead of being silently reset --
// so someone who gave up and walked away frees up the kiosk for the next
// guest, but someone who's just thinking gets a chance to say so.
const CONFIRM_PROMPT_PATHS = ['create-a-group', 'join-a-group'];
const IDLE_BEFORE_PROMPT = 60000; // no interaction for this long before we ask
const CONFIRM_WINDOW = 30000;     // then this long to respond before resetting anyway

// Everything else (nav, search, leaderboard, tap-to-group) keeps the original
// quiet behaviour -- there's no in-progress input to protect, so no prompt.
const DEFAULT_RESET_LENGTH = 30000;

// How often we check elapsed idle time.
const CHECK_INTERVAL = 5000;

// The arcade-redesign pages (2026-07-21) manage their own idle behaviour
// (register: 2-min reset; score-station: 30s display window; live boards:
// in-place data refresh) — the old wrapper must not touch them.
const SKIP_RESET_PATHS = ['', 'tap-to-group', 'register', 'live-board', 'live-board-portrait', 'score-station'];

const currentPath = () => window.location.pathname.split('/').join('');

const ResetWrapper = ({ children }) => {
    const [showConfirm, setShowConfirm] = useState(false);
    const lastInteractionAt = useRef(Date.now());
    const promptShownAt = useRef(null);
    const intervalRef = useRef(null);

    const doReset = () => {
        const path = currentPath();

        // Leaderboard is a passive/ambient display -- just refresh it.
        if (path === 'leadership-board') {
            window.location.reload();
            return;
        }

        // Mid card-tap lookup -- send back to the tap screen, not home.
        if (path === 'search-for-a-group' && window.location.search.split('?').join('').includes('cardNumber')) {
            window.location.href = '/tap-to-group';
            return;
        }

        window.location.href = '/';
    };

    const handleInteraction = () => {
        lastInteractionAt.current = Date.now();
        if (promptShownAt.current) {
            promptShownAt.current = null;
            setShowConfirm(false);
        }
    };

    const tick = () => {
        const path = currentPath();
        // Back-office admin (login + dashboard) is a team tool on a back-office
        // PC, NOT an ambient kiosk -- it must never auto-reset out from under a
        // team member who's reading the screen. currentPath() strips slashes,
        // so '/admin/dashboard/' -> 'admindashboard'; this covers every admin route.
        if (path.startsWith('admin')) return;
        if (SKIP_RESET_PATHS.includes(path)) return;

        const idleFor = Date.now() - lastInteractionAt.current;

        if (CONFIRM_PROMPT_PATHS.includes(path)) {
            if (promptShownAt.current) {
                if (Date.now() - promptShownAt.current >= CONFIRM_WINDOW) {
                    setShowConfirm(false);
                    doReset();
                }
                return;
            }
            if (idleFor >= IDLE_BEFORE_PROMPT) {
                promptShownAt.current = Date.now();
                setShowConfirm(true);
            }
            return;
        }

        if (idleFor >= DEFAULT_RESET_LENGTH) {
            doReset();
        }
    };

    useEffect(() => {
        const resetListeners = ['mousemove', 'keydown', 'touchstart', 'click'];
        resetListeners.forEach(event => {
            document.addEventListener(event, handleInteraction);
        });
        lastInteractionAt.current = Date.now();
        intervalRef.current = setInterval(tick, CHECK_INTERVAL);

        return () => {
            resetListeners.forEach(event => {
                document.removeEventListener(event, handleInteraction);
            });
            clearInterval(intervalRef.current);
        };
    }, []);

    return (
        <>
            {children}
            {showConfirm && (
                <div className="idle-confirm-overlay">
                    <div className="idle-confirm-box">
                        <p>Still there?</p>
                        <button type="button" onClick={handleInteraction}>Yes, I'm still here</button>
                    </div>
                </div>
            )}
        </>
    );
};

export default ResetWrapper;